import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * The end of the shell's header, for a page to hang its own buttons off.
 *
 * `undefined` is "there is no shell around this page" — the default, which only
 * happens when a page is rendered on its own. Inside the shell the value is the
 * header's slot, or null for the render before the ref lands.
 */
const HeaderSlotContext = createContext<HTMLElement | null | undefined>(undefined);

export function HeaderSlotProvider({
  slot,
  children,
}: {
  slot: HTMLElement | null;
  children: ReactNode;
}) {
  return <HeaderSlotContext.Provider value={slot}>{children}</HeaderSlotContext.Provider>;
}

/**
 * A page's own buttons, drawn at the right-hand end of the shell's header
 * instead of in the page body — the one row that is always in the same place,
 * whatever the page below has scrolled to.
 *
 * Written where the page would have drawn them and moved by a portal, so the
 * buttons stay with the page that owns them: their state, their dialogs and
 * their handlers are the page's, and they go when it does.
 *
 * A page rendered outside the shell has no header to move them into, and keeps
 * them where they are written — which is what a test of that page sees.
 */
export function HeaderActions({ children }: { children: ReactNode }) {
  const slot = useContext(HeaderSlotContext);

  if (slot === undefined) return <>{children}</>;
  return slot ? createPortal(children, slot) : null;
}
